import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import DraggableCart from './DraggableCart';
import Cart from './Cart';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContextNew';
import SignUpPromptModal from './SignUpPromptModal';

const GlobalCart = () => {
  const [showCart, setShowCart] = useState(false);
  const { totalItems } = useCart();
  const location = useLocation();
  const { isGuest, user, token } = useAuth();
  
  // Guest mode states
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);
  const [restrictedFeature, setRestrictedFeature] = useState('');
  
  // Force re-render trigger for auth state changes
  const [authCheck, setAuthCheck] = useState(0);
  
  // Check authentication - localStorage is single source of truth
  // Check localStorage FIRST (always current, updated immediately after signup)
  // Then fallback to context (for reactivity)
  const getIsAuthenticated = () => {
    // Check localStorage FIRST (always current, even if React state is stale)
    const storedToken = localStorage.getItem('token') || localStorage.getItem('customerToken');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      try {
        // Validate that storedUser is valid JSON
        JSON.parse(storedUser);
        return true;
      } catch {
        // Invalid JSON, treat as not authenticated
        return false;
      }
    }
    
    // Fallback to context only if localStorage is empty
    return !!(token && user);
  };
  
  const isAuthenticated = getIsAuthenticated();

  // Auth routes where cart should not be shown
  const authRoutes = ['/login', '/signup', '/forgot-password', '/guest'];
  const isAuthRoute = authRoutes.includes(location.pathname);
  
  // Pages that have their own cart implementation (currently none, GlobalCart is used everywhere)
  const pagesWithOwnCart = [];
  const hasOwnCart = pagesWithOwnCart.some(route => location.pathname.startsWith(route));

  // Guest mode handlers
  const handleGuestRestriction = (featureName) => {
    setRestrictedFeature(featureName);
    setShowSignUpPrompt(true);
  };

  // Listen for authentication state changes to force re-render
  useEffect(() => {
    const handleAuthStateChange = () => {
      // Force re-render to update isAuthenticated calculation
      setAuthCheck(prev => prev + 1);
    };

    // Listen for userAuthenticated event (dispatched after signup/login)
    window.addEventListener('userAuthenticated', handleAuthStateChange);
    
    return () => {
      window.removeEventListener('userAuthenticated', handleAuthStateChange);
    };
  }, []);

  // Listen for custom event to open cart - ALWAYS listen, even in guest mode
  // Empty dependencies - event handler always reads from localStorage directly
  useEffect(() => {
    const handleOpenCart = () => {
      // Always check localStorage directly (most reliable, always current)
      // Don't use getIsAuthenticated() function to avoid stale closure issues
      const storedToken = localStorage.getItem('token') || localStorage.getItem('customerToken');
      const storedUser = localStorage.getItem('user');
      
      let isAuth = false;
      if (storedToken && storedUser) {
        try {
          JSON.parse(storedUser); // Validate JSON
          isAuth = true;
        } catch {
          isAuth = false;
        }
      }
      
      if (!isAuth) {
        handleGuestRestriction('checkout');
      } else {
        setShowCart(true);
      }
    };

    window.addEventListener('openCart', handleOpenCart);
    return () => window.removeEventListener('openCart', handleOpenCart);
  }, []); // Empty deps - function reads from localStorage directly, not from closure

  // Don't show cart UI on auth routes
  if (isAuthRoute) {
    // Still render signup modal for guest mode cart clicks
    return (
      <SignUpPromptModal
        open={showSignUpPrompt}
        onClose={() => setShowSignUpPrompt(false)}
        featureName={restrictedFeature}
      />
    );
  }

  // Only show draggable cart button if there are items and not on pages with their own cart
  if (totalItems === 0 || hasOwnCart) {
    // Still render signup modal for guest mode
    return (
      <SignUpPromptModal
        open={showSignUpPrompt}
        onClose={() => setShowSignUpPrompt(false)}
        featureName={restrictedFeature}
      />
    );
  }

  const handleCartClick = () => {
    // Always check localStorage directly (most reliable, always current)
    const storedToken = localStorage.getItem('token') || localStorage.getItem('customerToken');
    const storedUser = localStorage.getItem('user');
    
    let isAuth = false;
    if (storedToken && storedUser) {
      try {
        JSON.parse(storedUser); // Validate JSON
        isAuth = true;
      } catch {
        isAuth = false;
      }
    }
    
    if (!isAuth) {
      handleGuestRestriction('checkout');
    } else {
      // Only open cart if it's not already open
      if (!showCart) {
        setShowCart(true);
      }
    }
  };

  return (
    <>
      {/* Draggable cart — guests see it too; tap opens signup prompt or cart */}
      <DraggableCart onCartClick={handleCartClick} />

      {/* Cart dialog — logged-in users only */}
      {isAuthenticated && <Cart open={showCart} onClose={() => setShowCart(false)} />}

      {/* Sign Up Prompt Modal for Guest Users */}
      <SignUpPromptModal
        open={showSignUpPrompt}
        onClose={() => setShowSignUpPrompt(false)}
        featureName={restrictedFeature}
      />
    </>
  );
};

export default GlobalCart; 